// @ts-nocheck
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Calendar,
  Clock,
  Plus,
  Trash2,
  CheckCircle,
  ArrowLeft,
  MessageSquare,
  Settings
} from "lucide-react";
import { useAuth } from "@/contexts/auth-context";

export function CreateOneOnOnePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Mock team members data - in real app would come from API
  const teamMembers = [
    { id: 'user-1', firstName: 'Sarah', lastName: 'Johnson', role: 'Frontend Developer' },
    { id: 'user-2', firstName: 'Mike', lastName: 'Chen', role: 'Backend Developer' },
    { id: 'user-3', firstName: 'Lisa', lastName: 'Rodriguez', role: 'UX Designer' },
    { id: 'user-4', firstName: 'David', lastName: 'Kim', role: 'Product Manager' },
  ];

  // Form state
  const [formData, setFormData] = useState({
    employeeId: '',
    scheduledDate: '',
    scheduledTime: '',
    duration: '',
    templateId: '',
    notes: '',
  });

  const [agendaItems, setAgendaItems] = useState<string[]>(['']);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddAgendaItem = () => {
    setAgendaItems([...agendaItems, '']);
  };

  const handleRemoveAgendaItem = (index: number) => {
    if (agendaItems.length > 1) {
      const newAgendaItems = [...agendaItems];
      newAgendaItems.splice(index, 1);
      setAgendaItems(newAgendaItems);
    }
  };

  const handleAgendaItemChange = (index: number, value: string) => {
    const newAgendaItems = [...agendaItems];
    newAgendaItems[index] = value;
    setAgendaItems(newAgendaItems);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.employeeId || !formData.scheduledDate || !formData.scheduledTime || !formData.duration) {
      alert('Please fill in all required fields');
      return;
    }

    if (agendaItems.filter(item => item.trim()).length === 0) {
      alert('Please add at least one agenda item');
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // In real app, would call API to create session
      const sessionData = {
        ...formData,
        agenda: agendaItems.filter(item => item.trim()),
        managerId: user?.id,
        status: 'scheduled',
        meetingType: 'video'
      };

      console.log('Creating session:', sessionData);

      // Navigate back to one-on-ones page
      navigate('/one-on-ones');
    } catch (error) {
      console.error('Failed to create session:', error);
      alert('Failed to create session. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl flex items-center justify-center">
              <Calendar className="h-8 w-8 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Schedule 1-on-1 Session</h1>
              <p className="text-muted-foreground text-lg">
                Create a meaningful one-on-one meeting with your team member
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/one-on-ones">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </Link>
          </div>
        </div>

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl flex items-center justify-center">
                <Settings className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Session Details</h3>
                <p className="text-sm text-muted-foreground font-normal">Configure the meeting settings and agenda</p>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="employeeId">Team Member *</Label>
                  <Select value={formData.employeeId} onValueChange={(value) => handleInputChange('employeeId', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select team member" />
                    </SelectTrigger>
                    <SelectContent>
                      {teamMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.firstName} {member.lastName} - {member.role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="duration">Duration *</Label>
                  <Select value={formData.duration} onValueChange={(value) => handleInputChange('duration', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">60 minutes</SelectItem>
                      <SelectItem value="90">90 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="scheduledDate">Date *</Label>
                  <Input
                    id="scheduledDate"
                    type="date"
                    value={formData.scheduledDate}
                    onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="scheduledTime">Time *</Label>
                  <Input
                    id="scheduledTime"
                    type="time"
                    value={formData.scheduledTime}
                    onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                  />
                </div>
              </div>

              {/* Agenda Items */}
              <div className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border border-indigo-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <Label className="text-lg font-semibold">Agenda Items *</Label>
                    <p className="text-sm text-muted-foreground">
                      Add topics you want to discuss during the session
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  {agendaItems.map((item, index) => (
                    <div key={index} className="flex gap-3">
                      <Input
                        value={item}
                        onChange={(e) => handleAgendaItemChange(index, e.target.value)}
                        placeholder={`Agenda item ${index + 1}`}
                        className="flex-1"
                      />
                      {agendaItems.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="text-destructive hover:bg-destructive/10"
                          onClick={() => handleRemoveAgendaItem(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="mt-4 border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                  onClick={handleAddAgendaItem}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Agenda Item
                </Button>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Preparation Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Add any notes or context for the session..."
                  rows={4}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-4 pt-6 border-t border-border">
                <Link to="/one-on-ones">
                  <Button variant="outline" disabled={isSubmitting}>
                    Cancel
                  </Button>
                </Link>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-current border-r-transparent rounded-full animate-spin" />
                      Creating...
                    </div>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Schedule Session
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
